import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * A typeable + select hybrid input ("combobox").
 *
 * - User can pick an option from the dropdown OR type a free-text value
 * - When `allowCustom` is true, the typed value is committed via onChange
 * - Each option: { value: string, label: string, sublabel?: string }
 *
 * Props:
 *   value         current text value
 *   onChange(v)   called with selected option value or typed value
 *   options       [{value, label, sublabel?}]
 *   placeholder
 *   allowCustom   default true — allows free-text values
 *   onCreate(v)   optional — when present and value isn't in options,
 *                 a "Create new: …" entry appears at the bottom
 *   className     wrapper style
 *   triggerTestId data-testid for the trigger button
 *   disabled
 */
export function Combobox({
  value, onChange, options = [], placeholder = "Type or pick…",
  allowCustom = true, onCreate, className, triggerTestId, disabled,
  emptyText = "No match — keep typing to use as free text",
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(value || "");

  React.useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label || value || "";

  const matched = draft
    ? options.filter((o) =>
        (o.label + " " + (o.sublabel || "") + " " + o.value).toLowerCase()
          .includes(draft.toLowerCase())
      )
    : options;

  const exact = options.find(
    (o) => o.label.toLowerCase() === draft.toLowerCase() ||
           o.value.toLowerCase() === draft.toLowerCase()
  );

  const commitFreeText = () => {
    if (allowCustom && draft && !exact) {
      onChange(draft);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={triggerTestId}
          className={cn("w-full justify-between font-normal", !value && "text-muted-foreground", className)}
        >
          <span className="truncate text-left">{displayLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={draft}
            onValueChange={setDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitFreeText();
              }
            }}
          />
          <CommandList>
            {matched.length === 0 && !allowCustom && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {matched.length > 0 && (
              <CommandGroup heading="From your library">
                {matched.slice(0, 50).map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    onSelect={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{o.label}</div>
                      {o.sublabel && <div className="text-[11px] text-muted-foreground truncate">{o.sublabel}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {allowCustom && draft && !exact && (
              <CommandGroup heading="Custom value">
                <CommandItem value={`__custom_${draft}`} onSelect={commitFreeText}>
                  <Plus className="mr-2 h-4 w-4" />
                  Use “{draft}” as free text
                </CommandItem>
              </CommandGroup>
            )}
            {onCreate && draft && !exact && (
              <CommandGroup heading="Create new">
                <CommandItem
                  value={`__create_${draft}`}
                  onSelect={() => {
                    onCreate(draft);
                    setOpen(false);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4 text-primary" />
                  <span className="text-primary">Create new: “{draft}”</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
